import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, finalize, map, of, Subscription, switchMap } from 'rxjs';

import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import { ContractorType, ContractorTypeRequirement } from 'src/app/models';
import {
  FilterBuilderOutput,
  FilterBuilderParam,
  FilterBuilderParamOpts,
  translateFilterBuilderToLegacy
} from 'src/app/models/filter-builder';
import {
  ContractorService,
  DataService,
  WorkflowContextService
} from 'src/app/services';
import { DataEntityFactory } from 'src/app/services/workflow.service';

import { MyContractorRegistrationActionCellComponent } from './my-contractor-registrations-action-cell/my-contractor-registration-action-cell.component';
import { ContractorRefreshService } from './my-contractor-registations.refresh.service';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

interface ContractorRegistrationRow {
  // Added by Amisha on 2026-07-23.
  // Reason: registration requirement template codes become dynamic row keys.
  [key: string]: any;

  id: string;
  contractorId?: string;
  contractorTypeId?: string;
  applicationId?: string;
  appNumber: string;
  name: string;
  business: string;
  address: string;
  phone: string;
  email: string;
  type: string;
  status?: string;
  actionLabel: string;
  canRenew: boolean;
  isDisabled?: boolean;
  isDeleted?:boolean;
}

interface SortOption {
  label: string;
  sortField: string;
  sortDescending: boolean;
}

@Component({
  selector: 'app-my-contractor-registrations',
  standalone: false,
  templateUrl: './my-contractor-registrations.component.html',
  styleUrls: ['./my-contractor-registrations.component.scss']
})
export class MyContractorRegistrationsComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  clientId = '';

  filterCriteria: FilterBuilderParam[] = [];
  workflowOpts: FilterBuilderParamOpts[] = [];
  filtersReady = false;

  filterTypes = [
    { name: 'contains' },
    { name: 'is' },
    { name: 'in' },
    { name: 'range' }
  ];
  statusOptions = [
    {
      name: 'Pending',
      value: '0'
    },
    {
      name: 'Approved',
      value: '1'
    },
    {
      name: 'Denied',
      value: '2'
    },
    {
      name: 'Expired',
      value: '3'
    },
    {
      name: 'Inactive',
      value: '4'
    },
    {
      name: 'Renewed',
      value: '6'
    },
    {
      name: 'Disabled',
      value: '7'
    }
  ];
  columns: TableColumn[] = [];
  displayedColumns: string[] = [];

  // Added by Amisha on 2026-07-23.
  // Reason: track requirement metadata so repeated paging requests preserve
  // the applicant's current visible-column choices.
  private requirementColumnSignature = '';
  private requirementColumnKeys = new Set<string>();

  data: ContractorRegistrationRow[] = [];

  pageSize = 10;
  currentPage = 0;
  totalPages = 0;
  totalRecords = 0;

  isLoading = true;

  sortField = 'registeredOn';
  sortDescending = true;
  isSortOpen = false;
  selectedSortLabel = 'All';

  activeFilters: FilterBuilderOutput = {};

  showOverlay = false;
  isOverlayOpen = false;
  isMobileDevice = false;

  renewingRegistrationId: string | null = null;
  startingRegistration = false;

  newRegistrationForm = new UntypedFormGroup({
    newRegContractorType: new UntypedFormControl(null, [Validators.required])
  });

  newRegistrationTypes: ContractorType[] | null = null;
  newRegistrationType: ContractorType | null = null;
  newRegistrationTypeId: string | null = null;

  selectedActionRow: ContractorRegistrationRow | null = null;
  currentAction: 'delete' | 'disable' | 'enable' | null = null;

  isRegistrationActionModalOpen = false;
  registrationModalTitle = '';
  registrationModalDesc = '';
  overlayOffsetTop = 0;
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-contractor-registration-applicant-janus';

  readonly sortOptions: SortOption[] = [
    {
      label: 'All',
      sortField: 'registeredOn',
      sortDescending: true
    },
    {
      label: 'Permit Type',
      sortField: 'contractorType',
      sortDescending: false
    },
    {
      label: 'Most Recent',
      sortField: 'registeredOn',
      sortDescending: true
    },
    {
      label: 'Incomplete',
      sortField: 'status',
      sortDescending: false
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private context: WorkflowContextService,
    private dataService: DataService,
    private contractorSvc: ContractorService,
    private refreshSvc: ContractorRefreshService
  ) {}

  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }
    this.updateViewportState();

    this.columns = this.buildColumns();
    this.displayedColumns = this.columns.map(column => column.key);

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.syncOverlayState();
        })
    );

    const parentParamSubscription = this.route.parent?.paramMap.subscribe(
      params => {
        this.clientId = params.get('clientId') || this.context.client?.id || '';

        if (!this.clientId) {
          return;
        }

        this.initFilters()
          .then(() => {
            /*
             * The filter builder emits filtersChange when initialized.
             * That event performs the initial search request.
             *
             * Do not call fetchRegistrations() here because it would
             * result in duplicate SearchContractorRegistrations calls.
             */
            this.filtersReady = true;
          })
          .catch(error => {
            console.error(
              'Unable to initialize contractor registration filters',
              error
            );
          });
      }
    );

    if (parentParamSubscription) {
      this.subscriptions.add(parentParamSubscription);
    }

    this.syncOverlayState();

    //added by priya for refersh trigger api on 07/08/2026
    this.subscriptions.add(
      this.refreshSvc.refresh$.subscribe(() => {
        this.fetchRegistrations(0);
      })
    );
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isSortOpen = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState();
    this.syncOverlayState();
  }

  toggleSort(event: MouseEvent): void {
    event.stopPropagation();
    this.isSortOpen = !this.isSortOpen;
  }

  selectSort(option: SortOption): void {
    this.sortField = option.sortField;
    this.sortDescending = option.sortDescending;
    this.selectedSortLabel = option.label;
    this.isSortOpen = false;

    this.fetchRegistrations(0);
  }

  filtersChanged(filters: FilterBuilderOutput): void {
    this.activeFilters = filters || {};
    this.fetchRegistrations(0);
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (!event.sortField || event.sortDescending === null) {
      return;
    }

    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;

    this.selectedSortLabel = this.getSortLabel(
      event.sortField,
      event.sortDescending
    );

    this.fetchRegistrations(this.currentPage);
  }

  onPageChange(page: number): void {
    this.fetchRegistrations(page);
  }
  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.fetchRegistrations(0);
  }

  // Added by Amisha on 2026-07-23.
  // Reason: keep the host's visibility state synchronized with Common Table.
  onDisplayedColumnsChange(displayedColumns: string[]): void {
    this.displayedColumns = [...displayedColumns];
  }

  onNewRegistrationClick(): void {
    this.isOverlayOpen = true;

    this.router.navigate(['new-registration'], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  get isNoData(): boolean {
    return !this.data || this.data.length === 0;
  }

  get hasFewData(): boolean {
    return !!this.data && this.data.length <= 4;
  }

  onRenew(row: ContractorRegistrationRow): void {
    if (!row?.id || !row.canRenew || this.renewingRegistrationId) {
      return;
    }

    this.renewingRegistrationId = row.id;

    this.subscriptions.add(
      this.dataService
        .startContractorRegistrationRenewal(
          row.id,
          row.contractorTypeId || '',
          false
        )
        .pipe(
          switchMap(startResult =>
            this.dataService
              .getCurrentApplicationActivity(startResult.applicationId)
              .pipe(
                map(activityResult => ({
                  applicationId: startResult.applicationId,
                  activityResult
                }))
              )
          ),
          finalize(() => {
            this.renewingRegistrationId = null;
          })
        )
        .subscribe({
          next: ({ applicationId, activityResult }) => {
            const activityDataId = this.getApplicationActivityDataId(
              activityResult
            );

            if (!activityDataId) {
              console.error(
                'Activity data ID was not returned for the renewal application.'
              );
              return;
            }

            this.router.navigate(
              ['workflow-application', applicationId, activityDataId],
              {
                relativeTo: this.route,
                queryParamsHandling: 'merge'
              }
            );
          },
          error: error => {
            console.error('Unable to start renewal flow', error);
          }
        })
    );
  }

  onViewDetails(row: ContractorRegistrationRow): void {
    if (!row?.id) {
      return;
    }

    this.router.navigate(['details', row.id], {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  onViewApplication(row: ContractorRegistrationRow): void {
    if (!row?.applicationId) {
      return;
    }

    const route =
      row.status === 'Pending'
        ? ['workflow-application', row.applicationId]
        : ['workflow-application-print-summary', row.applicationId];

    this.router.navigate(route, {
      relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  onDelete(row: ContractorRegistrationRow): void {
    if (!row?.id || row.isDeleted) {
      return;
    }

    this.openRegistrationAction(row, 'delete');
  }

  onDisable(row: ContractorRegistrationRow): void {
    if (!row?.id || row.isDisabled) {
      return;
    }

    this.openRegistrationAction(row, 'disable');
  }

  onEnable(row: ContractorRegistrationRow): void {
    if (!row?.id || !row.isDisabled) {
      return;
    }

    this.openRegistrationAction(row, 'enable');
  }
registrationActionModalData: CommonConfirmationModalData = {
  modalDescp: '',
  modalSubDescp: '',
  yesNoBtn: true,
  replaceYesWith: '',
  replaceNoWith: 'CANCEL',
  open: false
};
successModalData: CommonConfirmationModalData = {
  modalDescp: 'Success',
  modalSubDescp: '',
  yesNoBtn: false,
  open: false,
  okBtn:true
};
openRegistrationAction(
  row: ContractorRegistrationRow,
  action: 'delete' | 'disable' | 'enable'
): void {
  this.selectedActionRow = row;
  this.currentAction = action;

  if (action === 'delete') {
    this.registrationActionModalData = {
      modalDescp: 'Are you sure you want to delete this contractor registration?',
      modalSubDescp: '',
      yesNoBtn: true,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL',
      open: true
    };
  } else if (action === 'disable') {
    this.registrationActionModalData = {
      modalDescp: 'Are you sure you want to disable this contractor registration?',
      modalSubDescp: '',
      yesNoBtn: true,
      replaceYesWith: 'DISABLE',
      replaceNoWith: 'CANCEL',
      open: true
    };
  } else {
    this.registrationActionModalData = {
      modalDescp: 'Are you sure you want to enable this contractor registration?',
      modalSubDescp:
        'This registration will be restored to a status determined by its expiration date and/or requirements.',
      yesNoBtn: true,
      replaceYesWith: 'ENABLE',
      replaceNoWith: 'CANCEL',
      open: true
    };
  }
}
  confirmAction(): void {
  if (!this.selectedActionRow?.id || !this.currentAction) {
    return;
  }
  this.registrationActionModalData = {
    ...this.registrationActionModalData,
    open: false
  };
  const registrationId = this.selectedActionRow.id;
  const action = this.currentAction;

  this.isLoading = true;

  const request =
    action === 'delete'
      ? this.contractorSvc.deleteContractorRegistration(registrationId)
      : action === 'disable'
      ? this.contractorSvc.disableContractorRegistration(registrationId)
      : this.contractorSvc.reEnableContractorRegistration(registrationId);

  this.subscriptions.add(
    request
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.openSuccessModal(action);
          this.fetchRegistrations(this.currentPage);
        },
        error: error => {
          console.error(`Unable to ${action} registration`, error);
        }
      })
  );
}
closeSuccessModal(): void {
  this.successModalData = {
    ...this.successModalData,
    open: false
  };

  this.selectedActionRow = null;
  this.currentAction = null;
}

  


  openSuccessModal(action: 'delete' | 'disable' | 'enable'): void {
  const successMessage =
    action === 'delete'
      ? 'The registration has been deleted successfully.'
      : action === 'disable'
      ? 'The registration has been disabled successfully.'
      : 'The registration has been enabled successfully.';

   this.successModalData = {
    modalDescp: 'Success',
    modalSubDescp: successMessage,
    yesNoBtn: false,
    okBtn:true,
    open: true
  };
}
cancelAction(): void {
  this.registrationActionModalData = {
    ...this.registrationActionModalData,
    open: false
  };

  this.selectedActionRow = null;
  this.currentAction = null;
}

  closeModal(): void {
    this.selectedActionRow = null;
    this.currentAction = null;
  }

  

  private buildColumns(): TableColumn[] {
    return [
      {
        key: 'appNumber',
        label: 'App #',
        headerType: 'text',
        cellType: 'text',
        width: '110px'
      },
      {
        key: 'name',
        label: 'Name',
        headerType: 'text',
        cellType: 'text',
        width: '130px'
      },
      {
        key: 'business',
        label: 'Business',
        headerType: 'text',
        cellType: 'text',
        width: '150px'
      },
      {
        key: 'address',
        label: 'Addr.',
        headerType: 'text',
        cellType: 'text',
        width: '160px'
      },
      {
        key: 'phone',
        label: 'Phone',
        headerType: 'text',
        cellType: 'text',
        width: '120px'
      },
      {
        key: 'email',
        label: 'Email',
        headerType: 'text',
        cellType: 'text',
        width: '180px'
      },
      {
        key: 'type',
        label: 'Type',
        headerType: 'text',
        cellType: 'text',
        width: '130px'
      },
      {
        key: 'status',
        label: 'Status',
        headerType: 'text',
        cellType: 'text',
        width: '120px'
      },
      {
        key: 'actionLabel',
        label: 'Actions',
        headerType: 'text',
        cellType: 'component',
        component: MyContractorRegistrationActionCellComponent,
        componentInputs: (row: ContractorRegistrationRow) => ({
          row,
          viewDetails: () => this.onViewDetails(row),
          viewApplication: () => this.onViewApplication(row),
          renew: () => this.onRenew(row),
          delete: () => this.onDelete(row),
          disable: () => this.onDisable(row),
          enable: () => this.onEnable(row)
        }),
        width: '110px',
        sortable: false
      }
    ];
  }

  private mapRegistrationRow(registration: any): ContractorRegistrationRow {
    const contractor = registration.contractor || {};
    const registerApplication = registration.registerApplication || {};
    const type = registration.type || {};

    const status = registration.status;
    const statusText = this.getStatusText(status);

    const expiresOn =
      registration.expiresOn ||
      registration.expirationDate ||
      registration.expirationOn ||
      registration.expiration ||
      registration.expireDate ||
      registration.expiredOn ||
      null;

    const canRenew = this.isExpiredRegistration(status, expiresOn);

    const isDisabled = statusText.toLowerCase() === 'disabled';
    const isDeleted = statusText.toLowerCase() === 'deleted';

    const requirementValues = this.mapRequirementValues(registration);

    return {
      id: registration.id || registration.contractorRegistrationId || '',

      contractorId:
        registration.contractorId ||
        registration.contractorID ||
        contractor.id ||
        '',

      contractorTypeId:
        registration.contractorTypeId ||
        registration.contractorTypeID ||
        type.id ||
        '',

      applicationId:
        registration.registerApplicationId ||
        registerApplication.id ||
        registration.applicationId ||
        registration.workflowApplicationId ||
        '',

      appNumber:
        registerApplication.combinedApplicationNumber ||
        registerApplication.applicationNumber ||
        registration.applicationNumber ||
        registration.combinedApplicationNumber ||
        registration.appNumber ||
        registration.registrationNumber ||
        '',

      name: `${contractor.contactFirstName ||
        ''} ${contractor.contactLastName || ''}`.trim(),

      business: contractor.businessName || '',

      address:
        contractor.address?.address1 ||
        contractor.address?.fullAddress ||
        registration.address ||
        '',

      phone:
        contractor.businessPhone?.number ||
        registration.phone ||
        registration.phoneNumber ||
        '',

      email:
        contractor.contactEmail?.email ||
        registration.email ||
        registration.emailAddress ||
        '',

      type:
        type.name ||
        registration.contractorTypeName ||
        registration.workflowName ||
        '',

      status: statusText,
      actionLabel: 'Action',
      canRenew,
      isDisabled,
      isDeleted,
      ...requirementValues
    };
  }

  private setRequirementColumns(
    requirements: ContractorTypeRequirement[]
  ): void {
    const uniqueRequirements = (requirements || []).filter(
      (requirement, index, all) =>
        !!requirement?.templateCode &&
        all.findIndex(
          item => item?.templateCode === requirement.templateCode
        ) === index
    );

    const signature = uniqueRequirements
      .map(requirement => `${requirement.templateCode}:${requirement.label}`)
      .join('|');

    if (signature === this.requirementColumnSignature) {
      return;
    }

    const previouslyVisible = new Set(this.displayedColumns);

    const previousRequirementKeys = this.requirementColumnKeys;

    const actionColumn = this.columns.find(
      column => column.key === 'actionLabel'
    );

    const fixedColumns = this.columns
      .filter(
        column =>
          column.key !== 'actionLabel' &&
          !previousRequirementKeys.has(column.key)
      )
      .map(column => ({
        ...column,
        hidden: !previouslyVisible.has(column.key)
      }));

    const requirementColumns: TableColumn[] = uniqueRequirements.map(
      requirement => ({
        key: requirement.templateCode,
        label: requirement.label || requirement.templateCode,
        headerType: 'text',
        cellType: 'text',
        width: '180px',
        hidden:
          !previousRequirementKeys.has(requirement.templateCode) ||
          !previouslyVisible.has(requirement.templateCode)
      })
    );

    this.requirementColumnSignature = signature;

    this.requirementColumnKeys = new Set(
      uniqueRequirements.map(requirement => requirement.templateCode)
    );

    this.columns = [
      ...fixedColumns,
      ...requirementColumns,
      ...(actionColumn
        ? [
            {
              ...actionColumn,
              hidden: !previouslyVisible.has(actionColumn.key)
            }
          ]
        : [])
    ];

    this.displayedColumns = this.columns
      .filter(column => !column.hidden)
      .map(column => column.key);
  }

  private mapRequirementValues(registration: any): Record<string, string> {
    const values: Record<string, string> = Object.fromEntries(
      Array.from(this.requirementColumnKeys).map(key => [key, 'N/A'])
    );

    for (const requirement of registration?.type?.requirements || []) {
      const customField =
        (registration?.customFields || []).find(
          (field: any) => field.templateCode === requirement.templateCode
        ) ||
        (registration?.contractor?.customFields || []).find(
          (field: any) => field.templateCode === requirement.templateCode
        );

      values[requirement.templateCode] = this.formatCustomField(customField);
    }

    return values;
  }

  private formatCustomField(customField: any): string {
    if (!customField) {
      return 'N/A';
    }

    const dataEntityType =
      customField.dataEntityType ||
      customField.dataEntityDefinition?.dataEntityTypeCode;

    if (!dataEntityType) {
      return customField.fieldValue == null || customField.fieldValue === ''
        ? 'N/A'
        : String(customField.fieldValue);
    }

    try {
      const dataEntity = DataEntityFactory.createDataEntity(dataEntityType, {
        value: customField.fieldValue,
        label: customField.label,
        templateCode: customField.templateCode
      });

      return dataEntity.getFormattedValue() || 'N/A';
    } catch {
      return customField.fieldValue == null || customField.fieldValue === ''
        ? 'N/A'
        : String(customField.fieldValue);
    }
  }

  private isExpiredRegistration(status: any, expiresOn: any): boolean {
    const statusText = this.getStatusText(status).toLowerCase();

    if (statusText === 'expired') {
      return true;
    }

    if (!expiresOn) {
      return false;
    }

    const expirationDate = new Date(expiresOn);

    if (Number.isNaN(expirationDate.getTime())) {
      return false;
    }

    const today = new Date();

    today.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);

    return expirationDate < today;
  }

  private getStatusText(status: any): string {
    const statusMap: {
      [key: number]: string;
    } = {
      0: 'Pending',
      1: 'Approved',
      2: 'Denied',
      3: 'Expired',
      4: 'Inactive',
      5: 'Deleted',
      6: 'Renewed',
      7: 'Disabled'
    };

    return statusMap[Number(status)] || '';
  }

  private initFilters(): Promise<void> {
    const workflows$ = this.context.client
      ? this.dataService.getWorkflows(this.context.client, false)
      : this.dataService.getPublicWorkflows(null, false);

    const contractorWorkflows$ = this.context.client
      ? this.contractorSvc.getContractorRegistrationTypes(
          this.context.client.id
        )
      : of([]);

    return Promise.all([
      workflows$.toPromise(),
      contractorWorkflows$.toPromise()
    ]).then(([workflows, contractorWorkflows]) => {
      this.workflowOpts = [
        ...(workflows || []).map(workflow => ({
          name: workflow.version?.name || workflow.name,
          value: workflow.id
        })),

        ...(contractorWorkflows || []).map(contractorType => ({
          name: `${contractorType.name} (Registration)`,
          value: contractorType.registerWorkflowId
        })),

        ...(contractorWorkflows || []).map(contractorType => ({
          name: `${contractorType.name} (Renewal)`,
          value: contractorType.renewWorkflowId
        }))
      ]
        .filter(option => !!option.value)
        .sort((left, right) => left.name.localeCompare(right.name));

      /*
       * Filter attributes aligned with the visible table columns.
       * Actions is excluded because it is not searchable.
       */
      this.filterCriteria = [
        {
          name: 'App #',
          id: 'applicationNumber',
          types: ['contains', 'is']
        },
        {
          name: 'Name',
          id: 'contactName',
          types: ['contains', 'is']
        },
        {
          name: 'Business',
          id: 'businessName',
          types: ['contains', 'is']
        },
        {
          name: 'Addr.',
          id: 'address',
          types: ['contains', 'is']
        },
        {
          name: 'Phone',
          id: 'phone',
          types: ['contains', 'is']
        },
        {
          name: 'Email',
          id: 'email',
          types: ['contains', 'is']
        },
        {
          name: 'Type',
          id: 'type'
        },
        {
          name: 'Status',
          id: 'status',
          types: ['is', 'in'],
          options: this.statusOptions
        }
      ];
    });
  }

  private fetchRegistrations(page: number): void {
    if (!this.clientId) {
      return;
    }

    this.currentPage = page;
    this.isLoading = true;

    const filters = this.buildApiFilters();

    this.subscriptions.add(
      this.dataService
        .searchContractorRegistrations(
          this.clientId,
          this.sortField,
          this.sortDescending,
          filters,
          this.currentPage,
          this.pageSize,
          false
        )
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: result => {
            console.log('PAGE SIZE:', this.pageSize);
            console.log('CURRENT PAGE:', this.currentPage);
            console.log('TOTAL RECORDS:', result.recordCount);
            console.log('TOTAL PAGES:', result.pageCount);
            console.log('REGISTRATIONS:', result.contractorRegistrations);
            console.log(
              'REGISTRATION COUNT:',
              result.contractorRegistrations?.length
            );

            this.totalRecords = result.recordCount || 0;

            this.totalPages = Math.max(1, result.pageCount || 1);

            this.setRequirementColumns((result as any).requirements || []);

            const registrations =
              (result as any).contractorRegistrations ||
              (result as any).registrations ||
              [];

            this.data = registrations.map((registration: any) =>
              this.mapRegistrationRow(registration)
            );
          },
          error: error => {
            console.error('Unable to load contractor registrations', error);

            this.data = [];
            this.setRequirementColumns([]);
            this.totalRecords = 0;
            this.totalPages = 1;
          }
        })
    );
  }

  private buildApiFilters(): ItemSearchOptionField[] {
    const legacyFilters = translateFilterBuilderToLegacy(
      this.activeFilters || {}
    ) as ItemSearchOptionField[];

    const filters = [...legacyFilters];

    if (!filters.some(filterItem => filterItem.title === 'status')) {
      filters.push(this.buildContractorStatusFilter());
    }

    const userId = this.getCurrentUserId();

    if (userId && !filters.some(filterItem => filterItem.title === 'userId')) {
      filters.push(this.buildUserFilter(userId));
    }

    return filters;
  }

  private buildUserFilter(userId: string): ItemSearchOptionField {
    return {
      title: 'userId',
      options: [
        new ItemSearchOptionFieldOption({
          title: '',
          selected: true,
          filterText: '',
          searchText: userId,
          strOperator: 'is'
        })
      ]
    };
  }

  private getCurrentUserId(): string {
    return (
      (this.context as any).user?.id ||
      (this.context as any).userId ||
      (this.context as any).currentUser?.id ||
      ''
    );
  }

  private buildContractorStatusFilter(): ItemSearchOptionField {
    return {
      title: 'status',
      options: [
        new ItemSearchOptionFieldOption({
          title: '',
          selected: true,
          filterText: '',
          searchText: '0,1,2,4,6,7',
          strOperator: 'in'
        })
      ]
    };
  }

  private getSortLabel(sortField: string, sortDescending: boolean): string {
    const matchingOption = this.sortOptions.find(
      option =>
        option.sortField === sortField &&
        option.sortDescending === sortDescending
    );

    return matchingOption?.label || 'Custom';
  }

  private getApplicationActivityDataId(activityResult: any): string | null {
    if (activityResult?.activity?.activityDataId) {
      return activityResult.activity.activityDataId;
    }

    const formActivity = activityResult?.navigationItems?.find(
      (activity: any) => activity?.activityType === 'form-activity'
    );

    return formActivity?.activityDataId || null;
  }

  private updateViewportState(): void {
    this.isMobileDevice = window.innerWidth < 768;
  }

  private syncOverlayState(): void {
    const hasChildRoute = !!this.route.firstChild;

    if (this.isMobileDevice) {
      this.isOverlayOpen = hasChildRoute;
      this.showOverlay = false;
    } else {
      this.showOverlay = hasChildRoute;
      this.isOverlayOpen = false;
    }

    document.body.style.overflow = hasChildRoute ? 'hidden' : '';
  }
}
